import * as React from 'react';
import { compose, withHandlers, withState } from 'recompose';
import { reduxForm, change as reduxChange, getFormValues } from 'redux-form';
import { connect } from 'react-redux';
import { get, omit } from 'lodash';
import { Grid, Row, Col } from 'react-flexbox-grid';
import Divider from 'material-ui/Divider';
import { Card } from 'material-ui';
import Search from 'material-ui/svg-icons/action/search';
import Button from '@inqool/uas-web-core/es/components/controls/Button';

import {
  AutoComplete,
  AutoCompleteField,
  Input,
  InputField
  // Dočasně vypnuto spolu s ručním vyhledáváním CRZ zbraní.
  // Date as DateForm,
  // DateField,
} from '@components/form';
import { Loader } from '@components/loader';
import { crzLookupWeapons } from './_eventsActions';

const formName = 'module-events-weapons';

const getPersonFullName = person =>
  get(person, 'fullName') || [get(person, 'firstName'), get(person, 'lastName')].filter(Boolean).join(' ');

const hasPersonLookupData = person =>
  !!get(person, 'firstName') || !!get(person, 'lastName') || !!get(person, 'birthDate') || !!get(person, 'birthNumber');

export const canSearchCrzWeapons = (values = {}) => {
  const selectedParticipant = get(values, 'participant');
  const person = get(selectedParticipant, 'person') || get(values, 'owner', {});
  const hasLookupData = hasPersonLookupData(person);
  const requiresAifo = !!selectedParticipant;
  const hasAifo = !!get(person, 'aifo');
  const showPersonError = !hasLookupData;
  const showAifoError = hasLookupData && requiresAifo && !hasAifo;

  return {
    isValid: hasLookupData && !showAifoError,
    showPersonError,
    showAifoError
  };
};

const mapStateToProps = state => ({
  eventWeapons: get(state, 'form.module-events.values.weapons', []),
  participants: get(state, 'form.module-events.values.participants', []),
  values: getFormValues(formName)(state)
});

const mapDispatchToProps = dispatch => ({
  setEventWeapons: weapons => dispatch(reduxChange('module-events', 'weapons', weapons)),
  changeFieldValue: (field, value) => dispatch(reduxChange(formName, field, value))
});

const enhance = compose<any, any>(
  reduxForm({
    form: formName,
    keepDirtyOnReinitialize: true,
    enableReinitialize: true
  }),
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withState('loader', 'handleLoader', false),
  withState('showPersonError', 'handleShowPersonError', false),
  withState('showAifoError', 'handleShowAifoError', false),
  withState('weapons', 'handleWeapons', []),
  withState('crzResult', 'handleCrzResult', null),
  withHandlers({
    fillWeapon: ({ changeFieldValue }) => weapon => {
      Object.keys(weapon).forEach(attribute => {
        changeFieldValue(attribute, weapon[attribute]);
      });
    },
    addAllWeapons: ({ closeItem, eventWeapons = [], setEventWeapons, values = {} }) => weapons => {
      const owner = get(values, 'participant.person') || get(values, 'owner', {});
      setEventWeapons([...eventWeapons, ...weapons.map(weapon => ({ ...weapon, owner }))]);
      closeItem();
    },
    addWeapon: ({ onSubmit, values = {} }) => weapon => {
      const owner = get(values, 'participant.person') || get(values, 'owner', {});
      onSubmit(omit({ ...weapon, owner }, ['participant']));
    },
    searchCrz: ({
      values = {},
      handleLoader,
      handleWeapons,
      handleCrzResult,
      handleShowPersonError,
      handleShowAifoError
    }) => async () => {
      const person = get(values, 'participant.person') || get(values, 'owner', {});
      const validation = canSearchCrzWeapons(values);

      handleShowPersonError(validation.showPersonError);
      handleShowAifoError(validation.showAifoError);
      handleCrzResult(null);
      handleWeapons([]);
      if (!validation.isValid) {
        return;
      }

      handleLoader(true);
      const response = await crzLookupWeapons(person);
      handleCrzResult(response);
      handleWeapons(get(response, 'weapons', []));
      handleLoader(false);
    },
    onSubmit: ({ onSubmit }) => formData => {
      const owner = get(formData, 'participant.person') || get(formData, 'owner', {});
      onSubmit(omit({ ...formData, owner }, ['participant']));
    }
  })
);

const WeaponResult = ({ weapon, addWeapon, fillWeapon }) => (
  <Card style={{ padding: '15px', margin: '15px 0' }}>
    <Row style={{ paddingBottom: '15px' }}>
      {[
        { label: 'Číslo zbraně', value: get(weapon, 'weaponNumber') },
        { label: 'Výrobní číslo', value: get(weapon, 'serialNumber') },
        { label: 'Výrobce', value: get(weapon, 'manufacturer') },
        { label: 'Vzor', value: get(weapon, 'pattern') },
        { label: 'Ráže', value: get(weapon, 'caliber') },
        { label: 'Typ zbrojního listu', value: get(weapon, 'licenseType') },
        { label: 'Stav zbrojního listu', value: get(weapon, 'licenseState') }
      ].map(({ label, value }, key) => (
        <Col md={6} key={key} style={{ margin: '5px 0' }}>
          <Row>
            <Col md={6}>
              <div style={{ fontWeight: 'bold' }}>{label}:</div>
            </Col>
            <Col md={6}>
              <div style={{ textAlign: 'right' }}>{value || '-'}</div>
            </Col>
          </Row>
        </Col>
      ))}
    </Row>
    <Button label="Přidat" primary={true} onClick={() => addWeapon(weapon)} />
    <Button label="Předvyplnit" onClick={() => fillWeapon(weapon)} style={{ marginLeft: 15 }} />
  </Card>
);

const View: React.SFC<any> = ({
  handleSubmit,
  closeItem,
  searchCrz,
  participants = [],
  // values = {},
  weapons,
  crzResult,
  addWeapon,
  fillWeapon,
  addAllWeapons,
  loader,
  showPersonError,
  showAifoError
}) => {
  // Dočasně vypnuto spolu s ručním vyhledáváním CRZ zbraní.
  // const selectedParticipant = get(values, 'participant');

  return (
    <div>
      {loader && <Loader />}
      <form className="layout" onSubmit={handleSubmit}>
        <Grid fluid={true}>
          <Row>
            <Col className="grid-system-margin" />
          </Row>

          <Row>
            <Col md={12}>
              <h4>Lustrace CRZ</h4>
            </Col>
          </Row>

          <Row style={{ alignItems: 'center' }}>
            <Col md={6}>
              <AutoCompleteField
                component={AutoComplete}
                name="participant"
                label="Účastník"
                items={participants}
                labelFunction={o => getPersonFullName(get(o, 'person')) || 'Neznámý účastník'}
                valueFunction={o => o}
                fullWidth={true}
              />
            </Col>
            <Col md={6}>
              <Button label="Lustrovat CRZ" icon={<Search />} onClick={searchCrz} style={{ marginTop: 20 }} />
            </Col>
          </Row>

          {/* Dočasně vypnuto: vyhledávání CRZ zbraní přes ručně vyplněné údaje osoby.
              Aktuálně zůstává pouze výběr osoby ze selectu účastníků.
          {!selectedParticipant && (
            <React.Fragment>
              <Row>
                {[
                  { name: 'owner.firstName', label: 'Jméno' },
                  { name: 'owner.lastName', label: 'Příjmení' },
                  { name: 'owner.birthNumber', label: 'Rodné číslo' }
                ].map(field => (
                  <Col md={4} key={field.name}>
                    <InputField component={Input} {...field} />
                  </Col>
                ))}
              </Row>
              <Row>
                <Col md={4}>
                  <DateField component={DateForm} name="owner.birthDate" label="Datum narození" />
                </Col>
              </Row>
            </React.Fragment>
          )}
          */}

          {showPersonError && (
            <Row>
              <Col md={12}>
                <h5 style={{ color: 'red' }}>Vyberte účastníka nebo vyplňte údaje osoby pro lustraci CRZ</h5>
              </Col>
            </Row>
          )}
          {showAifoError && (
            <Row>
              <Col md={12}>
                <h5 style={{ color: 'red' }}>Pro lustraci CRZ musí mít osoba AIFO z předchozí lustrace osoby.</h5>
              </Col>
            </Row>
          )}

          {!!weapons.length && (
            <Row>
              <Col md={12}>
                <Button
                  label="Přidat všechny nalezené zbraně"
                  primary={true}
                  onClick={() => addAllWeapons(weapons)}
                  style={{ margin: '15px 0' }}
                />
                {weapons.map((weapon, index) => (
                  <WeaponResult
                    key={`${get(weapon, 'id', index)}-${get(weapon, 'serialNumber', index)}`}
                    weapon={weapon}
                    addWeapon={addWeapon}
                    fillWeapon={fillWeapon}
                  />
                ))}
              </Col>
            </Row>
          )}

          {crzResult &&
            !weapons.length && (
              <Row>
                <Col md={12}>
                  <Card style={{ padding: '15px', margin: '15px 0' }}>
                    <h5>Osoba není držitelem zbrojního oprávnění nebo nebyly nalezeny žádné zbraně.</h5>
                    <Row>
                      <Col md={6}>
                        <div style={{ fontWeight: 'bold' }}>Typ zbrojního listu:</div>
                      </Col>
                      <Col md={6}>
                        <div style={{ textAlign: 'right' }}>{get(crzResult, 'licenseType') || '-'}</div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <div style={{ fontWeight: 'bold' }}>Stav zbrojního listu:</div>
                      </Col>
                      <Col md={6}>
                        <div style={{ textAlign: 'right' }}>{get(crzResult, 'licenseState') || '-'}</div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            )}

          <Row>
            <Col md={12}>
              <h4>Zbraň</h4>
            </Col>
          </Row>

          <Row>
            {[
              { name: 'weaponNumber', label: 'Číslo zbraně' },
              { name: 'serialNumber', label: 'Výrobní číslo' },
              { name: 'manufacturer', label: 'Výrobce' },
              { name: 'pattern', label: 'Vzor' },
              { name: 'caliber', label: 'Ráže' },
              { name: 'licenseType', label: 'Typ zbrojního listu' },
              { name: 'licenseState', label: 'Stav zbrojního listu' },
              { name: 'note', label: 'Poznámka' }
            ].map(field => (
              <Col md={4} key={field.name}>
                <InputField component={Input} {...field} />
              </Col>
            ))}
          </Row>
        </Grid>

        <Divider />

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button type="submit" label="Uložit a zavřít" primary={true} style={{ margin: '16px 0 16px 16px' }} />
          <Button label="Zrušit" style={{ margin: '16px 0 16px 16px' }} onClick={closeItem} />
        </div>
      </form>
    </div>
  );
};

export const EventsFormParticipantsWeaponsForm = enhance(View);



// WEBPACK FOOTER //
// ./src/modules/events/EventsFormParticipantsWeaponsForm.tsx